import React, { useState, useEffect } from "react";
import Leftsidebar from "../sidebar/leftsidebar";
import LeftResponsivesidebar from "../sidebar/left-responsive";
import Headmenu from "../sidebar/headmenu";
import { FaShoppingCart } from "react-icons/fa";
import { LuUndo2, LuFileText } from "react-icons/lu";
import { PiVan } from "react-icons/pi";
import { OrderTrack } from "./OrderTrack";
import { useNavigate } from "react-router-dom";
import { getCart } from "../../api/cartApi";
import { fetchOwnProfile } from "../../utils/auth";
import defaultAvatar from "../../assets/profile.png";
import LiveShopColumn from "../marketplace/LiveShopColumn";
import { COUNTRY_OPTIONS, countryName } from "../../utils/address";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
const ORDERS_PAGE_SIZE = 10;
const CURRENCY_SYMBOLS = { usd: "$", inr: "₹", eur: "€", gbp: "£" };
const currencySymbolFor = (order) => CURRENCY_SYMBOLS[order?.seller?.currency?.toLowerCase()] || "$";
const authHeaders = () => {
  const token = sessionStorage.getItem("accessToken") || localStorage.getItem("accessToken");
  return { "Content-Type": "application/json", ...(token ? { token } : {}) };
};

const formatDate = (value, utc = false) =>
  value
    ? new Date(value).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        ...(utc ? { timeZone: "UTC" } : {}),
      })
    : "N/A";

// Map an order from POST /order onto the shape the order card and OrderTrack render
const normalizeOrder = (o) => {
  const items = o.orderItems?.length
    ? o.orderItems
    : o.product
      ? [{
          id: `order-${o.id}`,
          product_id: o.product_id,
          quantity: o.quantity,
          unit_price: o.unit_price,
          total_price: o.total_price,
          taxable_amount: o.taxable_amount,
          tax_rate: o.tax_breakdown?.[0]?.rate,
          tax_amount: o.tax_amount,
          product: o.product,
        }]
      : [];
  return {
    ...o,
    order_id: o.id,
    order_status: o.status,
    pricing: {
      subtotal: Number(o.total_price) || 0,
      delivery_charges: Number(o.delivery_charges) || 0,
      tax_amount: Number(o.tax_amount) || 0,
      tax_breakdown: o.tax_breakdown || [],
      final_amount: Number(o.final_amount) || 0,
    },
    order_items: items.map((it) => ({
      ...it,
      product_name: it.product_name || it.product?.product_name,
      image: it.image || it.product?.product_media?.[0]?.url,
    })),
  };
};

const escapeHtml = (value) =>
  String(value ?? "").replace(/[&<>"']/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch]));

// Printable invoice for one order (the browser's print dialog can save it as PDF); amounts and tax are the
// ones stored with the order at checkout, never recalculated
const openInvoice = (order) => {
  const win = window.open("", "_blank");
  if (!win) {
    alert("Please allow pop-ups to view the invoice.");
    return;
  }
  const ship = order.shipping_address || {};
  const bill = order.billing_address || ship;
  const sym = currencySymbolFor(order);
  const money = (value) => `${sym}${Number(value || 0).toFixed(2)}`;
  const addressHtml = (a) =>
    [a.name || a.full_name, a.address_line_1, a.address_line_2, [a.city, a.state, a.postal_code].filter(Boolean).join(" "), a.country && countryName(a.country)]
      .filter(Boolean)
      .map(escapeHtml)
      .join("<br>");
  const paidAt = (order.history || []).filter((h) => h.status === "confirmed").map((h) => h.created_at).pop();
  const rows = (order.order_items || [])
    .map(
      (it) => `<tr><td>${escapeHtml(it.product_name || "Product")}</td><td>${escapeHtml(it.quantity)}</td>
        <td>${money(it.unit_price)}</td><td>${money(it.total_price)}</td><td>${money(it.taxable_amount)}</td>
        <td>${Number(it.tax_rate || 0)}%</td><td>${money(it.tax_amount)}</td></tr>`
    )
    .join("");
  const taxRows = order.pricing.tax_breakdown
    .map(
      (rule) => `<tr><td style="text-align:right">${escapeHtml(rule.name)} (${escapeHtml(rule.tax_type)} ${rule.rate}% on ${money(rule.taxable_amount)})</td>
        <td>${money(rule.tax_amount)}</td></tr>${rule.components
        .map((c) => `<tr class="muted"><td style="text-align:right">${escapeHtml(c.name)} (${c.rate}%)</td><td>${money(c.amount)}</td></tr>`)
        .join("")}`
    )
    .join("");
  win.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Invoice ${escapeHtml(order.order_number)}</title>
    <style>body{font-family:Arial,sans-serif;color:#161F28;padding:32px;max-width:900px;margin:auto}
    h1{color:#095228;margin:0 0 4px}table{width:100%;border-collapse:collapse;margin-top:16px}
    th,td{border-bottom:1px solid #e5e7eb;padding:8px;text-align:left}.totals td{border:none;padding:4px 8px}
    .muted{color:#6b7280;font-size:13px}.parties{display:flex;gap:32px;flex-wrap:wrap}.parties p{flex:1;min-width:200px}</style></head><body>
    <h1>Invoice</h1><div class="muted">Nyawoo Marketplace</div>
    <p><b>Invoice no.:</b> ${escapeHtml(String(order.order_number || order.id).replace(/^ORD-/, "INV-"))}
    <br><b>Invoice date:</b> ${escapeHtml(formatDate(paidAt || order.created_at))}
    <br><b>Order no.:</b> ${escapeHtml(order.order_number || order.id)}<br><b>Order date:</b> ${escapeHtml(formatDate(order.created_at))}
    <br><b>Status:</b> ${escapeHtml(order.status)} / payment ${escapeHtml(order.payment_status)}</p>
    <div class="parties">
    <p><b>Sold by:</b><br>${[order.seller?.name || "-", order.seller?.location, order.seller?.country].filter(Boolean).map(escapeHtml).join("<br>")}</p>
    <p><b>Bill to:</b><br>${addressHtml(bill)}</p><p><b>Ship to:</b><br>${addressHtml(ship)}</p></div>
    <table><thead><tr><th>Item</th><th>Qty</th><th>Unit price</th><th>Amount</th><th>Taxable amount</th><th>Tax rate</th><th>Tax</th></tr></thead>
    <tbody>${rows}</tbody></table>
    <table class="totals"><tr><td style="text-align:right">Subtotal</td><td style="width:120px">${money(order.pricing.subtotal)}</td></tr>
    <tr><td style="text-align:right">Delivery</td><td>${money(order.pricing.delivery_charges)}</td></tr>
    ${taxRows}<tr><td style="text-align:right">Total tax</td><td>${money(order.pricing.tax_amount)}</td></tr>
    <tr><td style="text-align:right"><b>Grand total</b></td><td><b>${money(order.pricing.final_amount)}</b></td></tr></table>
    <script>window.onload=function(){window.print()}</script></body></html>`);
  win.document.close();
};

const OrderPage = () => {
  const navigate = useNavigate();
  const [cartCount, setCartCount] = useState(0);
  const [tabs, setTabs] = useState(1);
  const [orders, setOrders] = useState([]);
  const [ordersTotal, setOrdersTotal] = useState(0);
  const [ordersPage, setOrdersPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [walletBalance, setWalletBalance] = useState(null);

  const tabslist = [
    { id: 1, name: "My Orders" },
    { id: 2, name: "My Address" },
    { id: 3, name: "Wallet" },
  ];

  // Real order history from the API (newest first)
  useEffect(() => {
    let active = true;
    const loadOrders = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`${API_BASE_URL}/order`, {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify({ page: ordersPage, limit: ORDERS_PAGE_SIZE }),
        });
        const data = await response.json();
        if (!active) return;
        if (response.ok && data.success) {
          const pageOrders = (data.result?.orders || []).map(normalizeOrder);
          setOrders((prev) => {
            const base = ordersPage === 1 ? [] : prev;
            const seen = new Set(base.map((o) => o.id));
            return [...base, ...pageOrders.filter((o) => !seen.has(o.id))];
          });
          setOrdersTotal(Number(data.result?.total) || 0);
        } else {
          setError(data.message || "Failed to load orders.");
        }
      } catch (err) {
        if (active) setError("Failed to load orders.");
      } finally {
        if (active) setLoading(false);
      }
    };
    loadOrders();
    return () => {
      active = false;
    };
  }, [ordersPage]);

  // Cart badge on the View Cart button
  useEffect(() => {
    getCart()
      .then((res) => setCartCount(Number(res?.summary?.total_items) || (res?.cart_items || []).length || 0))
      .catch(() => setCartCount(0));
  }, []);

  // Wallet tab loads on first open
  useEffect(() => {
    if (tabs === 3 && walletBalance === null) {
      fetch(`${API_BASE_URL}/wallet/balance`, { headers: authHeaders() })
        .then((res) => res.json())
        .then((data) => setWalletBalance(data?.success ? Number(data.result?.balance) || 0 : 0))
        .catch(() => setWalletBalance(0));
    }
  }, [tabs, walletBalance]);

  return (
    <div className="dashboard w-full min-h-screen flex">
      <Leftsidebar />

      <main className="flex-1 min-w-0 p-4 md:p-6 md:pl-8">
        <LeftResponsivesidebar />
        <Headmenu />

        {/* Top actions */}
        <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-[#095228] font-semibold"
          >
            <span className="w-7 h-7 rounded-md bg-[#E3EFE7] flex items-center justify-center">
              <LuUndo2 />
            </span>
            BACK
          </button>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/myprofile")}
              className="px-5 py-2 bg-[#095228] text-white rounded-full hover:bg-green-800 transition text-sm font-medium"
            >
              My Profile
            </button>
            <button
              onClick={() => navigate("/cart")}
              className="relative px-5 py-2 bg-[#095228] text-white rounded-full hover:bg-green-800 transition flex items-center gap-2 text-sm font-medium"
            >
              <FaShoppingCart />
              View Cart
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 min-w-[20px] h-5 px-1 text-xs flex items-center justify-center text-white bg-yellow-500 rounded-full">
                  {cartCount}
                </span>
              )}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-6">
          {/* Left: profile, tabs, content */}
          <div className="lg:col-span-2 min-w-0">
            <ProfileHeader />

            <div className="flex gap-4 lg:gap-8 mt-8">
              {tabslist.map((res) => (
                <button
                  key={res.id}
                  onClick={() => setTabs(res.id)}
                  className={`flex-1 lg:flex-none lg:w-[160px] border-b-[3px] text-start text-base md:text-lg font-semibold py-2 whitespace-nowrap transition duration-200 ${
                    res.id === tabs ? "border-[#095228] text-[#161F28]" : "border-[#D9D9D9] text-gray-500"
                  }`}
                >
                  {res.name}
                </button>
              ))}
            </div>

            {tabs === 1 ? (
              <div className="space-y-5 mt-6">
                {error ? (
                  <div className="bg-red-50 text-red-600 p-6 rounded-xl">{error}</div>
                ) : loading && orders.length === 0 ? (
                  <div className="text-center p-6 text-gray-500">Loading orders...</div>
                ) : orders.length === 0 ? (
                  <div className="text-center p-6 text-gray-500">You haven't placed any orders yet.</div>
                ) : (
                  <>
                    {orders.map((order) => (
                      <OrderSummary key={order.id} order={order} />
                    ))}
                    {orders.length < ordersTotal && (
                      <div className="text-center">
                        <button
                          type="button"
                          disabled={loading}
                          onClick={() => setOrdersPage((p) => p + 1)}
                          className="px-5 py-2 border border-[#095228] text-[#095228] rounded-full hover:bg-green-50 disabled:opacity-60"
                        >
                          {loading ? "Loading..." : "Load more orders"}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            ) : tabs === 2 ? (
              <AddressesTab />
            ) : (
              tabs === 3 && (
                <div className="mt-6">
                  <div className="bg-gradient-to-r from-cyan-400 to-green-400 rounded-2xl px-6 py-6 flex flex-wrap items-center justify-between gap-4">
                    <div>
                      <p className="text-white text-sm font-medium opacity-80">Current Balance</p>
                      <p className="text-white text-3xl font-semibold mt-1">
                        {walletBalance === null ? "..." : `$ ${walletBalance.toFixed(2)}`}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => navigate("/wallet")}
                      className="bg-white text-green-800 font-semibold px-5 py-2.5 rounded-full shadow hover:bg-gray-50"
                    >
                      Open Wallet
                    </button>
                  </div>
                </div>
              )
            )}
          </div>

          {/* Right: live shopping */}
          <LiveShopColumn />
        </div>
      </main>
    </div>
  );
};

export default OrderPage;

const EMPTY_ADDRESS = {
  full_name: "",
  email: "",
  confirmation_email: "",
  phone_number: "",
  house_number_street: "",
  city: "",
  region: "",
  postal_code: "",
  country: "",
  is_default: false,
};

// Same rules as the API's createBuyerAddressValidation, so problems show before submitting
const validateAddress = (a) => {
  const errors = {};
  if (!a.full_name.trim()) errors.full_name = "Full name is required";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(a.email.trim())) errors.email = "Please provide a valid email address";
  if (a.confirmation_email.trim() !== a.email.trim()) errors.confirmation_email = "Email and confirmation email must match";
  const phone = a.phone_number.trim();
  if (phone.length < 10 || phone.length > 20 || !/^[\d\s()+.-]+$/.test(phone))
    errors.phone_number = "Please provide a valid phone number (10-20 digits)";
  if (!a.house_number_street.trim()) errors.house_number_street = "House number and street name is required";
  if (!a.city.trim()) errors.city = "City is required";
  if (!a.region.trim()) errors.region = "Region is required";
  if (!a.country) errors.country = "Country is required";
  const postal = a.postal_code.trim();
  if (postal.length < 3 || postal.length > 20 || !/^[A-Za-z0-9\s-]+$/.test(postal))
    errors.postal_code = "Please provide a valid postal code";
  return errors;
};

const ADDRESS_FIELDS = [
  { name: "full_name", label: "Full Name", placeholder: "Enter full name" },
  { name: "phone_number", label: "Phone Number", placeholder: "Enter phone number", type: "tel" },
  { name: "email", label: "Email", placeholder: "Enter email", type: "email" },
  { name: "confirmation_email", label: "Confirm Email", placeholder: "Re-enter email", type: "email" },
  { name: "house_number_street", label: "House Number and Street Name", placeholder: "Enter house number and street", wide: true },
  { name: "city", label: "City", placeholder: "Enter city" },
  { name: "region", label: "State / Region", placeholder: "Enter state or region" },
  { name: "postal_code", label: "Postal Code", placeholder: "Enter postal code" },
  { name: "country", label: "Country", options: COUNTRY_OPTIONS },
];

// Saved buyer addresses: list, add a new one, choose the default
const AddressesTab = () => {
  const [addresses, setAddresses] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_ADDRESS);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [defaultingId, setDefaultingId] = useState(null);

  const loadAddresses = () =>
    fetch(`${API_BASE_URL}/buyeraddress`, { headers: authHeaders() })
      .then((res) => res.json())
      .then((data) => setAddresses(Array.isArray(data?.result) ? data.result : []))
      .catch(() => setAddresses([]));

  useEffect(() => {
    loadAddresses();
  }, []);

  const handleField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setMessage(null);
    const found = validateAddress(form);
    setErrors(found);
    if (Object.keys(found).length) return;

    setSaving(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(form).map(([key, value]) => [key, typeof value === "string" ? value.trim() : value])
      );
      const response = await fetch(`${API_BASE_URL}/buyeraddress`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setForm(EMPTY_ADDRESS);
        setShowForm(false);
        setMessage({ type: "success", text: "Address added successfully." });
        await loadAddresses();
      } else {
        setMessage({ type: "error", text: data.message || "Failed to add address." });
      }
    } catch (err) {
      setMessage({ type: "error", text: "Failed to add address. Please try again." });
    } finally {
      setSaving(false);
    }
  };

  const handleSetDefault = async (addressId) => {
    setMessage(null);
    setDefaultingId(addressId);
    try {
      const response = await fetch(`${API_BASE_URL}/buyeraddress/${addressId}/set-default`, {
        method: "PATCH",
        headers: authHeaders(),
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setMessage({ type: "success", text: "Default address updated." });
        await loadAddresses();
      } else {
        setMessage({ type: "error", text: data.message || "Failed to set default address." });
      }
    } catch (err) {
      setMessage({ type: "error", text: "Failed to set default address. Please try again." });
    } finally {
      setDefaultingId(null);
    }
  };

  return (
    <div className="mt-6 space-y-4">
      <div className="flex justify-end">
        {!showForm && (
          <button
            type="button"
            onClick={() => {
              setShowForm(true);
              setMessage(null);
            }}
            className="px-5 py-2 bg-[#095228] text-white rounded-full text-sm font-medium hover:bg-green-800"
          >
            + Add New Address
          </button>
        )}
      </div>

      {message && (
        <p className={`text-sm ${message.type === "success" ? "text-green-700" : "text-red-600"}`}>{message.text}</p>
      )}

      {showForm && (
        <form onSubmit={handleSave} noValidate className="bg-white rounded-2xl border border-[#E5E5E5] p-5 space-y-4">
          <h3 className="font-semibold text-[#161F28]">Add New Address</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {ADDRESS_FIELDS.map((field) => (
              <div key={field.name} className={field.wide ? "md:col-span-2" : ""}>
                <label htmlFor={`address-${field.name}`} className="block text-sm font-medium mb-1">
                  {field.label}<span className="text-red-500">*</span>
                </label>
                {field.options ? (
                  <select
                    id={`address-${field.name}`}
                    value={form[field.name]}
                    onChange={(e) => handleField(field.name, e.target.value)}
                    className={`w-full border rounded-lg p-3 outline-none bg-white focus:ring-2 focus:ring-green-200 ${
                      errors[field.name] ? "border-red-500" : "border-gray-300"
                    }`}
                  >
                    <option value="">Select {field.label.toLowerCase()}</option>
                    {field.options.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    id={`address-${field.name}`}
                    type={field.type || "text"}
                    value={form[field.name]}
                    onChange={(e) => handleField(field.name, e.target.value)}
                    placeholder={field.placeholder}
                    className={`w-full border rounded-lg p-3 outline-none focus:ring-2 focus:ring-green-200 ${
                      errors[field.name] ? "border-red-500" : "border-gray-300"
                    }`}
                  />
                )}
                {errors[field.name] && <p className="text-xs text-red-600 mt-1">{errors[field.name]}</p>}
              </div>
            ))}
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={form.is_default}
              onChange={(e) => handleField("is_default", e.target.checked)}
              className="accent-[#095228]"
            />
            Set as default address
          </label>
          <div className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={() => {
                setShowForm(false);
                setForm(EMPTY_ADDRESS);
                setErrors({});
              }}
              className="px-5 py-2 border border-gray-300 rounded-full text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-5 py-2 bg-[#095228] text-white rounded-full text-sm font-medium hover:bg-green-800 disabled:opacity-60"
            >
              {saving ? "Saving..." : "Save Address"}
            </button>
          </div>
        </form>
      )}

      {addresses === null ? (
        <p className="text-gray-500">Loading addresses...</p>
      ) : addresses.length === 0 ? (
        !showForm && <p className="text-gray-500">No saved addresses yet. Add one to use it at checkout.</p>
      ) : (
        addresses.map((a) => (
          <div key={a.id} className="bg-white rounded-2xl border border-[#E5E5E5] p-5 flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <p className="font-semibold">{a.full_name}</p>
                {a.is_default && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">Default</span>
                )}
              </div>
              <p className="text-sm text-gray-700">
                {[a.house_number_street, a.city, a.region, a.postal_code].filter(Boolean).join(", ")}
              </p>
              {a.phone_number && <p className="text-sm text-gray-500 mt-1">Phone: {a.phone_number}</p>}
              {a.email && <p className="text-sm text-gray-500">Email: {a.email}</p>}
            </div>
            {!a.is_default && (
              <button
                type="button"
                disabled={defaultingId !== null}
                onClick={() => handleSetDefault(a.id)}
                className="px-4 py-1.5 border border-[#095228] text-[#095228] rounded-full text-sm hover:bg-green-50 disabled:opacity-60"
              >
                {defaultingId === a.id ? "Saving..." : "Set as default"}
              </button>
            )}
          </div>
        ))
      )}
    </div>
  );
};

// Logged-in user's picture, name, username and follower preview
const ProfileHeader = () => {
  const [profile, setProfile] = useState(null);
  const [followers, setFollowers] = useState([]);

  useEffect(() => {
    const token = sessionStorage.getItem("accessToken") || localStorage.getItem("accessToken");
    if (!token) return;
    let active = true;
    fetchOwnProfile(token)
      .then((res) => res.json())
      .then((data) => active && data?.result && setProfile(data.result))
      .catch(() => {});
    fetch(`${API_BASE_URL}/friend/get-friends`, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({ filters: { allFollowers: true }, page: 1, limit: 4 }),
    })
      .then((res) => res.json())
      .then((data) => active && setFollowers((data?.result?.friends || []).map((f) => f.User1).filter(Boolean)))
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  if (!profile) return null;
  const name =
    profile.account_type === "business"
      ? profile.business_name || "N/A"
      : `${profile.first_name || ""} ${profile.last_name || ""}`.trim() || profile.username;
  const followersCount = Number(profile.followersCount) || 0;

  return (
    <div className="flex items-center gap-4">
      <img
        src={profile.profile_picture_url || defaultAvatar}
        alt={name}
        className="w-[72px] h-[72px] rounded-full object-cover border-2 border-orange-300"
      />
      <div>
        <p className="font-semibold text-lg text-[#161F28]">{name}</p>
        <p className="text-sm text-gray-500">@{profile.username}</p>
        <div className="flex items-center mt-1">
          {followers.map((f) => (
            <img
              key={f.id}
              src={f.profile_picture_url || defaultAvatar}
              alt={f.username || "Follower"}
              title={`${f.first_name || ""} ${f.last_name || ""}`.trim() || f.username}
              className="w-5 h-5 rounded-full object-cover border-2 border-white -ml-1.5 first:ml-0"
            />
          ))}
          <span className={`text-xs italic font-semibold text-[#095228] ${followers.length ? "ml-2" : ""}`}>
            {followersCount} {followersCount === 1 ? "Follower" : "Followers"}
          </span>
        </div>
      </div>
    </div>
  );
};

const OrderSummary = ({ order }) => {
  const [images, setImages] = useState({}); // map productId -> imageUrl

  // Fetch product images for items without one
  useEffect(() => {
    if (!order?.order_items?.length) return;

    const idsToFetch = new Set();
    (order.order_items || []).forEach((it) => {
      if (!it.image && !images[it.product_id]) idsToFetch.add(it.product_id);
    });
    if (idsToFetch.size === 0) return;

    const fetchForId = async (productId) => {
      try {
        const res = await fetch(`${API_BASE_URL}/product?id=${encodeURIComponent(productId)}`, {
          headers: authHeaders(),
        });
        if (!res.ok) return null;
        const json = await res.json();
        return json?.result?.product_media?.[0]?.url || json?.result?.image || null;
      } catch (e) {
        return null;
      }
    };

    let mounted = true;
    (async () => {
      const updates = {};
      for (const id of idsToFetch) {
        if (images[id]) continue;
        const img = await fetchForId(id);
        if (img && mounted) updates[id] = img;
      }
      if (mounted && Object.keys(updates).length) {
        setImages((prev) => ({ ...prev, ...updates }));
      }
    })();

    return () => {
      mounted = false;
    };
  }, [order, images]);

  const orderDate = formatDate(order?.created_at);

  // The seller-set estimate is a plain calendar day stored at midnight UTC, so it is read back as a
  // UTC date; the 30-days-from-order fallback is a real instant and keeps normal local-time display.
  const deliveryDate = order?.estimate_delivery_date
    ? formatDate(order.estimate_delivery_date, true)
    : order?.created_at
      ? formatDate(new Date(order.created_at).getTime() + 30 * 24 * 60 * 60 * 1000)
      : "N/A";

  const status = order?.order_status || "new";
  // Once an order is cancelled, returned or delivered, an *estimated* delivery date no longer applies
  const finalStatusAt = (wanted) =>
    (order?.history || []).filter((h) => h.status === wanted).map((h) => h.created_at).pop();
  const deliveryLine = ["cancelled", "returned"].includes(status)
    ? {
        text: `${status === "cancelled" ? "Cancelled" : "Returned"}${finalStatusAt(status) ? ` on ${formatDate(finalStatusAt(status))}` : ""}`,
        className: "text-red-600",
      }
    : status === "delivered"
      ? { text: `Delivered${finalStatusAt("delivered") ? ` on ${formatDate(finalStatusAt("delivered"))}` : ""}`, className: "text-green-600" }
      : { text: `Estimated delivery: ${deliveryDate}`, className: "text-green-600" };
  const statusClass = ["delivered", "confirmed"].includes(status)
    ? "bg-green-100 text-green-700"
    : ["cancelled", "returned"].includes(status)
      ? "bg-red-100 text-red-700"
      : status === "new"
        ? "bg-blue-100 text-blue-700"
        : "bg-yellow-100 text-yellow-700";

  return (
    <div className="bg-white rounded-2xl border border-[#E5E5E5] p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="text-[#98911F] font-semibold text-lg break-all">
          Order ID: {order?.order_number || `#${order?.order_id || "N/A"}`}
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => openInvoice(order)}
            className="flex items-center gap-2 px-3 py-2 border border-[#095228] rounded-lg hover:bg-green-50 text-[#095228] text-sm font-medium"
          >
            <LuFileText />
            Invoice
          </button>
          <OrderTrack order={order} />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-3 text-sm">
        <span className="text-gray-500">
          Order date: <span className="font-semibold text-[#161F28] ml-1">{orderDate}</span>
        </span>
        <span className="hidden sm:block h-4 border-l border-gray-300" />
        <span className={`flex items-center gap-1.5 font-medium ${deliveryLine.className}`}>
          <PiVan className="text-base" />
          {deliveryLine.text}
        </span>
        <span className={`ml-auto px-3 py-0.5 rounded-full text-xs font-medium capitalize ${statusClass}`}>
          {status.replace(/_/g, " ")}
          {order?.payment_status === "pending" && status === "new" ? " · payment pending" : ""}
        </span>
      </div>

      <div className="divide-y mt-3">
        {(order?.order_items || []).map((item) => {
          const src = item.image || images[item.product_id] || "/image/shoeimg.png";
          const size = item.size || item.variant?.size;
          const color = item.color || item.variant?.color;
          return (
            <div key={item.id} className="flex items-center gap-4 py-3">
              <div className="w-[72px] h-[72px] rounded-lg bg-[#F2F2F2] flex items-center justify-center flex-shrink-0">
                <img src={src} alt={item.product_name || "Product"} className="w-full h-full object-contain rounded-lg" />
              </div>
              <div className="min-w-0">
                <h3 className="font-semibold text-[#161F28]">{item.product_name || "Product"}</h3>
                {size && <p className="text-xs text-gray-500">Size: <span className="text-gray-700">{size}</span></p>}
                {color && <p className="text-xs text-gray-500">Color: <span className="text-gray-700">{color}</span></p>}
                <p className="text-xs text-gray-500">
                  Qty: <span className="text-gray-700">{item.quantity}</span>
                  <span className="mx-2">·</span>
                  <span className="text-gray-700">{currencySymbolFor(order)}{Number(item.total_price || item.unit_price || 0).toFixed(2)}</span>
                </p>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex justify-end mt-2 text-sm">
        <span className="text-gray-500 mr-2">Total</span>
        <span className="font-semibold text-[#161F28]">{currencySymbolFor(order)}{order.pricing.final_amount.toFixed(2)}</span>
      </div>
    </div>
  );
};
